// MasterViewPage / masterViewDefs 向けの名前解決・整形ヘルパ。

import { ReactNode } from "react";
import { Chip } from "@mui/material";
import { ExtendedDB } from "../../../types/extendedDB";
import { MasterLink, MasterEntityType } from "../../../components/links/EntityLinks";
import { readProp } from "../../../util/typed";

export function findById<T extends { id: number | string }>(rows: T[], id?: number | null): T | undefined {
  if (id == null) {
    return undefined;
  }
  return rows.find((row) => row.id === id);
}

function nameOr(row: { name?: string } | undefined, fallback: string = "-"): string {
  if (row?.name) {
    return row.name;
  }
  return fallback;
}

export function matName(db: ExtendedDB, id?: number | null): string {
  return nameOr(findById(db.materials, id));
}
export function wtName(db: ExtendedDB, id?: number | null): string {
  return nameOr(findById(db.workTypes, id));
}
export function wstName(db: ExtendedDB, id?: number | null): string {
  return nameOr(findById(db.workSubtypes, id));
}
export function wdName(db: ExtendedDB, id?: number | null): string {
  return nameOr(findById(db.workDetails, id));
}
export function cmName(db: ExtendedDB, id?: number | null): string {
  return nameOr(findById(db.constructionMethods, id));
}
export function smName(db: ExtendedDB, id?: number | null): string {
  return nameOr(findById(db.stirringMethods, id));
}
export function adName(db: ExtendedDB, id?: number | null): string {
  return nameOr(findById(db.areaDivisions, id));
}
export function cliName(db: ExtendedDB, id?: number | null): string {
  return nameOr(findById(db.clients, id));
}
export function mdivName(db: ExtendedDB, id?: number | null): string {
  return nameOr(findById(db.materialDivisions, id));
}
export function mdName(db: ExtendedDB, id?: number | null): string {
  const row = findById(db.materialDetails, id);
  if (row?.detail_name) {
    return row.detail_name;
  }
  return "-";
}

// 配合比の表示文字列。definition の materialDetails から「主剤 1 : 硬化剤 0.5」風の文字列を組み立てる。
export function mixRatioDisplayName(db: ExtendedDB, def: { id: number; material_id: number } | undefined): string {
  if (!def) {
    return "-";
  }
  const details = db.mixRatioDetails.filter((d) => d.mix_ratio_definition_id === def.id && d.deleted_at == null);
  if (details.length === 0) {
    return matName(db, def.material_id);
  }
  const parts = details.map((d) => {
    const detail = findById(db.materialDetails, d.material_detail_id);
    const label = detail?.detail_name ?? "-";
    const ratio = d.ratio_end != null ? `${d.ratio_start}〜${d.ratio_end}` : `${d.ratio_start}`;
    return `${label} ${ratio}`;
  });
  return parts.join(" : ");
}

export function mixLabelOrNull(db: ExtendedDB, id?: number | null): string | null {
  if (id == null) {
    return null;
  }
  const def = findById(db.mixRatioDefinitions, id);
  if (!def) {
    return null;
  }
  return mixRatioDisplayName(db, def);
}

// 工事型区分のスロット名 (AreaDivisionLocationLabel から解決)。slot は 1..N の整数。
export function slotLabelFor(db: ExtendedDB, areaDivisionId: number | undefined, slot: number | undefined): string {
  if (areaDivisionId == null || slot == null) {
    return `区分${slot ?? "?"}`;
  }
  const label = db.areaDivisionLocationLabels.find(
    (l) => l.area_division_id === areaDivisionId && l.slot === slot && l.deleted_at == null,
  );
  if (label?.label_name) {
    return label.label_name;
  }
  return `区分${slot}`;
}

// 場所区分の親パス文字列。LocationDivisionParentPath を slot 順に並べた表示。
export function formatParentPath(db: ExtendedDB, locationDivisionId: number): string {
  const paths = db.locationDivisionParentPaths
    .filter((p) => p.location_division_id === locationDivisionId && p.deleted_at == null)
    .sort((a, b) => a.ancestor_slot - b.ancestor_slot);
  if (paths.length === 0) {
    return "";
  }
  return paths.map((p) => p.ancestor_division_name).join(" / ");
}

export function checkOrDash(flag?: boolean): string {
  if (flag) {
    return "✓";
  }
  return "-";
}

export function checkOrCross(flag?: boolean): ReactNode {
  if (flag) {
    return <Chip size="small" color="success" label="✓" />;
  }
  return <Chip size="small" color="default" label="✗" />;
}

export function linkOr(master: MasterEntityType, id: number | null | undefined, label: ReactNode, fallback: string = "-"): ReactNode {
  return <MasterLink type={master} id={id ?? null} name={label} fallback={fallback} />;
}

export function rangeOrDash(lower: number | null | undefined, upper: number | null | undefined, unit: string = ""): string {
  const l = lower != null ? `${lower}${unit}` : "-";
  const u = upper != null ? `${upper}${unit}` : "-";
  return `${l} 〜 ${u}`;
}

// 論理削除を除外して指定フィールドで親 ID と一致する行を返す。
export function activeChildrenBy<T extends { deleted_at?: number | null }>(
  rows: T[],
  filterField: string,
  parentId: number | string | undefined,
): T[] {
  return rows.filter((r) => {
    if (r.deleted_at != null) {
      return false;
    }
    if (readProp(r, filterField) !== parentId) {
      return false;
    }
    return true;
  });
}
